import * as Sentry from '@sentry/nextjs'
import { debounce, get, isEmpty } from 'lodash-es'
import {
  FC,
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { LuCirclePlus, LuEye, LuPencil, LuTrash2, LuTriangleAlert } from 'react-icons/lu'
import scrollParent from 'scrollparent'

import {
  AsideList,
  AsideListItem,
  GeoFenceCreateEdit,
  ObservatoryContext,
  SearchSection,
  usePersistentObservatoryPageStore,
} from '@/feature/observatory'
import { GeoFence } from '@/feature/observatory/types/GeoFence.types'
import { toaster } from '@/components/Toaster'
import { GeoFenceEventsBreakdown } from '@/feature/observatory/types/GeoFenceEvent.types'
import { FeatureFlag, useFlag, WhenFlag } from '@/feature/feature-flags'
import { EtaLocation, EtaLocationType } from '@/feature/eta'
import { useAssetTelemetry } from '@/feature/telemetry'
import { reverseGeocode } from '@/feature/map'
import { getLongitudeLatitudeFromGeoFence } from '@/feature/map/utils/getLongitudeLatitudeFromGeoFence'
import { GEOFENCE_SORT_OPTIONS } from '@/feature/observatory/filters'

import { Loader } from '@/lib/core/components/Loader'
import { Tooltip } from '@/lib/core/components/Tooltip'

import { GeoFenceDeleteDialog } from './GeoFenceDeleteDialog'
import styles from './GeoFencesAside.module.css'

const GeoFenceListItemBindingRef = (
  {
    geoFenceId,
    title,
    address,
    inside,
    latitude,
    longitude,
    isHighRisk,
    isActive,
    onClick,
    onEdit,
    onDelete,
    onMoreInfo,
    canShowEta,
    hasAlerts,
  }: {
    geoFenceId: number
    title: string
    address: string
    latitude: number
    longitude: number
    inside: number
    isHighRisk: boolean
    isActive: boolean
    onClick: () => void
    onEdit: () => void
    onDelete: () => void
    onMoreInfo: () => void
    canShowEta: boolean
    hasAlerts: boolean
  },
  ref: ForwardedRef<HTMLLIElement>,
) => {
  const selectedItem = usePersistentObservatoryPageStore(({ selectedItem }) => selectedItem)
  const setSelectedItem = usePersistentObservatoryPageStore(
    ({ setSelectedItem }) => setSelectedItem,
  )

  const { assets } = useContext(ObservatoryContext)

  const setEtaLocationFrom = usePersistentObservatoryPageStore(({ eta }) => eta.setEtaLocationFrom)
  const setEtaLocationTo = usePersistentObservatoryPageStore(({ eta }) => eta.setEtaLocationTo)
  const setShowEta = usePersistentObservatoryPageStore(({ eta }) => eta.setShowEta)

  const searchLocation = usePersistentObservatoryPageStore((state) => state.searchLocation)
  const setSearchLocation = usePersistentObservatoryPageStore((state) => state.setSearchLocation)

  const locationTo = {
    id: geoFenceId,
    title: title,
    latitude: latitude,
    longitude: longitude,
    address: address,
    highRiskArea: isHighRisk,
    type: EtaLocationType.GEOFENCE,
  } as EtaLocation

  const isSelectedAsset = useMemo(() => {
    return assets.data.results.find((asset) => asset.id === selectedItem?.id)
  }, [assets])

  const assetTrackingData = useAssetTelemetry(isSelectedAsset?.vehicleBridgeId)

  const reverseGeocodeMode = useFlag('reverseGeocodeMode', 'Google' as string)

  const etaClickHandler = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      let locationFrom: EtaLocation | undefined = undefined

      if (isSelectedAsset && assetTrackingData) {
        const currentLocation = await reverseGeocode(
          assetTrackingData.lat,
          assetTrackingData.lon,
          reverseGeocodeMode,
        )

        if (currentLocation !== undefined) {
          locationFrom = {
            id: isSelectedAsset.id,
            title: isSelectedAsset.description,
            plateNumber: isSelectedAsset.plateNumber,
            avatar: isSelectedAsset.owner?.avatar,
            address: currentLocation,
            latitude: assetTrackingData.latitude,
            longitude: assetTrackingData.longitude,
            type: EtaLocationType.ASSET,
          }
        }
      }

      if (locationFrom == undefined && searchLocation !== undefined) {
        locationFrom = {
          address: searchLocation.address,
          latitude: searchLocation.location.lat,
          longitude: searchLocation.location.lng,
          type: EtaLocationType.CUSTOM,
        }
      }
      if (locationFrom !== undefined) {
        setEtaLocationFrom(locationFrom)
        setEtaLocationTo(locationTo)
        setSelectedItem(undefined)
        setShowEta(true)
        setSearchLocation(undefined)
      }
    },
    [isSelectedAsset, assetTrackingData, searchLocation],
  ) as unknown as MouseEventHandler<HTMLDivElement>

  return (
    <AsideListItem
      ref={ref}
      onClick={onClick}
      active={isActive}
      className={styles.geofenceItemContainer}
      header={
        <div className={styles.geoFenceHeader}>
          <div className={styles.geoFenceTitle} title={title}>
            {title}
          </div>
          <div className={styles.geoFenceAddress} title={address}>
            {address}
          </div>
        </div>
      }
      footer={
        <div className={styles.geoFenceFooter}>
          <button
            type="button"
            onClick={onMoreInfo}
            className={[
              styles.baseGeofenceActionButton,
              styles.geofenceAssetsInsideButton,
              isActive ? styles.active : '',
            ].join(' ')}
          >
            <span>{inside} Assets Inside</span>
            <LuEye size={13} />
          </button>
          <FeatureFlag flag={'featureEta'}>
            <WhenFlag>
              {canShowEta && (searchLocation || (assetTrackingData && isSelectedAsset)) && (
                <div
                  role={'button'}
                  onClick={etaClickHandler}
                  className={[styles.baseGeofenceActionButton, isActive ? styles.active : ''].join(
                    ' ',
                  )}
                >
                  ETA
                </div>
              )}
            </WhenFlag>
          </FeatureFlag>
          <div className={styles.baseGeofenceActionsButtonContainer}>
            <button
              type="button"
              className={[styles.baseGeofenceActionButton, isActive ? styles.active : ''].join(' ')}
              onClick={(e) => {
                e.stopPropagation()
                onEdit()
              }}
              aria-label="Edit"
              data-testid="geofence-edit-button"
            >
              <LuPencil size={16} />
            </button>
            {hasAlerts ? (
              <Tooltip
                trigger="mouseenter"
                placement="bottom-end"
                tooltipContent="Remove alerts before deleting"
              >
                <button
                  type="button"
                  className={[
                    styles.baseGeofenceActionButton,
                    styles.geoFenceContains,
                    isActive ? styles.active : '',
                  ].join(' ')}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Delete"
                  data-testid="geofence-delete-button"
                  disabled
                >
                  <LuTrash2 size={16} />
                </button>
              </Tooltip>
            ) : (
              <button
                type="button"
                className={[
                  styles.baseGeofenceActionButton,
                  styles.geoFenceContains,
                  isActive ? styles.active : '',
                ].join(' ')}
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
                aria-label="Delete"
                data-testid="geofence-delete-button"
              >
                <LuTrash2 size={16} />
              </button>
            )}
          </div>
        </div>
      }
      icon={
        isHighRisk ? (
          <div className={styles.highRiskBadge}>
            <LuTriangleAlert size={14} />
            <span>High Risk</span>
          </div>
        ) : null
      }
    />
  )
}

export const GeoFenceListItemBinding = forwardRef(GeoFenceListItemBindingRef)

export const GeoFenceListItem = ({
  geoFence,
  onEdit,
  events,
}: {
  geoFence: GeoFence
  onEdit: (g: GeoFence) => void
  events?: GeoFenceEventsBreakdown
}) => {
  const { deleteGeoFence } = useContext(ObservatoryContext)
  const selectedItem = usePersistentObservatoryPageStore(({ selectedItem }) => selectedItem)
  const setSelectedItem = usePersistentObservatoryPageStore(
    ({ setSelectedItem }) => setSelectedItem,
  )
  const setGeoFenceModalId = usePersistentObservatoryPageStore(
    ({ setGeoFenceModalId }) => setGeoFenceModalId,
  )

  const isActive = selectedItem?.type === 'geoFence' && selectedItem?.id === geoFence.id
  const baseElement = useRef<HTMLLIElement>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (isActive && baseElement.current) {
      scrollParent(baseElement.current)?.scrollTo({
        top: baseElement.current.offsetTop,
        behavior: 'smooth',
      })
    }
  }, [isActive, baseElement.current])

  const { lng, lat } = useMemo(() => {
    if (geoFence) {
      return getLongitudeLatitudeFromGeoFence(geoFence)
    } else return { lng: 0, lat: 0 }
  }, [geoFence])

  const canShowEta = get(geoFence, 'geoJson.geometry.type') !== 'LineString'

  const setSelectedGeofence = () => {
    setSelectedItem({
      id: geoFence.id,
      type: 'geoFence',
    })
  }

  return (
    <>
      <GeoFenceListItemBinding
        ref={baseElement}
        geoFenceId={geoFence.id}
        title={geoFence.title}
        address={geoFence.address}
        latitude={lat}
        longitude={lng}
        isHighRisk={geoFence.highRiskArea}
        isActive={isActive}
        inside={geoFence.insideCount || (events?.inside?.length ?? 0)}
        onClick={() => {
          setSelectedGeofence()
        }}
        onEdit={() => {
          setSelectedGeofence()
          onEdit(geoFence)
        }}
        onDelete={() => setShowDeleteConfirm(true)}
        onMoreInfo={() => setGeoFenceModalId(geoFence.id)}
        canShowEta={canShowEta}
        hasAlerts={geoFence.alertRules.length > 0}
      />

      <GeoFenceDeleteDialog
        isOpen={showDeleteConfirm}
        geofenceTitle={geoFence.title}
        isDeleting={isDeleting}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true)
          try {
            await deleteGeoFence(geoFence.id)
            toaster({ message: 'Successfully Deleted GeoFence', type: 'success' })
            setShowDeleteConfirm(false)
          } catch (err) {
            Sentry.captureException(err)
            console.error(err)
            toaster({ message: 'Error Deleting GeoFence', type: 'error' })
          } finally {
            setIsDeleting(false)
          }
        }}
      />
    </>
  )
}

const GeoFenceList = ({
  geoFences,
  onEdit,
}: {
  geoFences: GeoFence[]
  onEdit: (g: GeoFence) => void
}) => {
  const { geoFenceEvents } = useContext(ObservatoryContext)

  return (
    <AsideList items={geoFences} keyExtractor={(geoFence) => geoFence.id}>
      {(geoFence) => {
        const events = geoFenceEvents[geoFence.id]

        return (
          <GeoFenceListItem key={geoFence.id} geoFence={geoFence} onEdit={onEdit} events={events} />
        )
      }}
    </AsideList>
  )
}

const GeoFenceMain = ({
  setSubScreen,
}: {
  setSubScreen: (subScreen: keyof typeof subScreenSelector, props?: Record<string, unknown>) => void
}) => {
  const { geoFences, geoFenceEvents } = useContext(ObservatoryContext)
  const geoFenceFilters = usePersistentObservatoryPageStore((state) => state.geoFenceFilters)
  const geoFenceSearch = usePersistentObservatoryPageStore((state) => state.geoFenceSearch)
  const setGeoFenceFilters = usePersistentObservatoryPageStore((state) => state.setGeoFenceFilters)
  const setGeoFenceSearch = usePersistentObservatoryPageStore((state) => state.setGeoFenceSearch)

  const [localSearch, setLocalSearch] = useState(geoFenceSearch)
  const debouncedSetGeoFenceSearch = useMemo(
    () => debounce((value: string) => setGeoFenceSearch(value), 500),
    [setGeoFenceSearch],
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value)
      debouncedSetGeoFenceSearch(value)
    },
    [debouncedSetGeoFenceSearch],
  )

  const geoFencesData = useMemo(() => {
    // FIXME: switch to use plain sort() when we have access to the creation date from the backend
    return geoFences.data?.results?.slice().sort((a, b) => {
      switch (geoFenceFilters.sortOn) {
        case 'High-Risk':
          return b.highRiskArea ? 1 : -1
        case 'Recently Active': {
          const aEvents = geoFenceEvents[`${a.title}/${a.id}`]
          const bEvents = geoFenceEvents[`${a.title}/${a.id}`]

          if (aEvents == null && bEvents == null) {
            return 0
          } else if (aEvents == null) {
            return -1
          } else if (bEvents == null) {
            return 1
          } else if (aEvents.records.length == 0 && bEvents.records.length == 0) {
            return 0
          } else if (aEvents.records.length == 0 && bEvents.records.length != 0) {
            return -1
          } else if (aEvents.records.length != 0 && bEvents.records.length == 0) {
            return 1
          }

          // This makes me sad to have to do instead of the input being validated :(
          // Since this happens irregularly, I'm going to leave the data handling for now

          const aRecords = aEvents.records.map((record) => {
            return new Date((record.exit ?? record.enter).timestamp).getTime()
          })
          const bRecords = bEvents.records.map((record) => {
            return new Date((record.exit ?? record.enter).timestamp).getTime()
          })

          const aLastEvent = aRecords.slice().sort((a, b) => b - a)[0]
          const bLastEvent = bRecords.slice().sort((a, b) => b - a)[0]

          return bLastEvent - aLastEvent
        }
        case 'Date Modified': {
          const aDate = new Date(a.modifiedAt).getTime()
          const bDate = new Date(b.modifiedAt).getTime()
          return bDate - aDate
        }
        case 'Alphabetically':
          return a.title.localeCompare(b.title)
        default:
          return 0
      }
    })
  }, [geoFences.data?.results, geoFenceFilters.sortOn])

  return (
    <div className={styles.geoFencesMainAside}>
      <div className={styles.geoFencesAsideHeader}>
        <h1 className={styles.heading}>Geofences</h1>
        <button
          type="button"
          name="add-geo-fence"
          aria-label="add-geo-fence"
          className={styles.addGeoFenceButton}
          onClick={() => setSubScreen('create')}
        >
          <LuCirclePlus size={18} />
          New
        </button>
      </div>
      <SearchSection
        search={localSearch}
        onSearchChanged={handleSearchChange}
        filters={geoFenceFilters}
        onFilterChanged={setGeoFenceFilters}
        sortOptions={GEOFENCE_SORT_OPTIONS}
      />
      <hr className={styles.searchDivider} />

      {geoFences.error ? (
        <div className={styles.centerText}>Failed to load geo fences, please try again</div>
      ) : geoFences.isLoading ? (
        <div className={styles.centerText}>
          <Loader />
        </div>
      ) : isEmpty(geoFencesData) ? (
        <div className={styles.centerText}>No geofences</div>
      ) : (
        <GeoFenceList
          geoFences={geoFencesData!}
          onEdit={(geoFence) => setSubScreen('edit', { geoFence })}
        />
      )}
    </div>
  )
}

const subScreenSelector = {
  main: GeoFenceMain,
  create: GeoFenceCreateEdit,
  edit: GeoFenceCreateEdit,
} as Record<
  'main' | 'create' | 'edit',
  FC<{
    setSubScreen: (
      subScreen: keyof typeof subScreenSelector,
      props?: Record<string, unknown>,
    ) => void
  }>
>

export const GeoFencesAside = ({
  onSubscreenChange,
}: {
  onSubscreenChange?: (screen: keyof typeof subScreenSelector) => void
}) => {
  const [subScreen, setSubScreen] = useState<keyof typeof subScreenSelector>('main')
  const [subScreenProps, setSubScreenProps] = useState<Record<string, unknown>>({})
  const [fade, setFade] = useState(false)

  const SubScreen = subScreenSelector[subScreen]

  // When a geofence edit is requested via persistent store, auto-open the edit screen
  const { geoFences } = useContext(ObservatoryContext)
  const geoFenceEditId = usePersistentObservatoryPageStore((s) => s.geoFenceEditId)
  const setGeoFenceEditId = usePersistentObservatoryPageStore((s) => s.setGeoFenceEditId)
  useEffect(() => {
    if (geoFenceEditId == null) return
    const list = geoFences.data?.results
    if (!list || list.length === 0) return
    const target = list.find((g) => g.id === geoFenceEditId)
    if (!target) return
    setSubScreen('edit')
    setSubScreenProps({ geoFence: target })
    // Clear the flag so it doesn't keep re-triggering
    setGeoFenceEditId(undefined)
  }, [geoFenceEditId, geoFences.data?.results])

  useEffect(() => {
    if (subScreen) {
      onSubscreenChange?.(subScreen)
    }
  }, [subScreen])

  return (
    <aside className={styles.geoFencesAside}>
      <div
        className={[
          styles.geoFencesAsideTransition,
          'animate__animated',
          fade ? 'animate__fadeOutRight' : 'animate__fadeInLeft',
        ].join(' ')}
      >
        <SubScreen
          setSubScreen={(subscreen, props) => {
            setFade(true)
            setTimeout(() => {
              setSubScreen(subscreen)
              setSubScreenProps(props ?? {})
              setFade(false)
            }, 300)
          }}
          {...subScreenProps}
        />
      </div>
    </aside>
  )
}
